import { CircleDot, Diamond, Flag, Square } from "lucide-react"
import type { ReactNode } from "react"

/**
 * El flujo de un proceso, dibujado.
 *
 * ⚠ **Es una vista, no el modelo.** El proceso sigue siendo una LISTA ORDENADA en la
 * base: un paso de decisión anota sus dos ramas como texto. Modelar un grafo de verdad
 * —aristas, ciclos, un lienzo con arrastre— es otro proyecto, y para lo que esto tiene
 * que contestar —«¿qué hace este cargo?»— el orden basta. Este diagrama pinta esa
 * lista de arriba abajo, que es como se lee un procedimiento.
 *
 * ⚠ **Las ramas Sí/No se escriben AL LADO del rombo, no como flechas.** Con flechas
 * habría que decidir a qué paso salta cada una, y eso es exactamente el grafo que el
 * modelo no tiene: dibujarlo obligaría a inventar destinos que nadie ha declarado.
 *
 * ⚠ **Cada forma lleva su etiqueta escrita.** El rombo no puede ser la única señal de
 * que un paso es una decisión (WCAG 1.4.1): en una impresión en gris todas las cajas
 * se parecen.
 */

type Paso = {
  id: number | string
  tipo: string
  titulo: string
  descripcion?: string | null
  responsable?: string | null
  rama_si?: string | null
  rama_no?: string | null
}

const FORMA: Record<string, { icono: ReactNode; etiqueta: string; color: string }> = {
  INICIO: { icono: <CircleDot size={14} />, etiqueta: "Inicio", color: "var(--success)" },
  TAREA: { icono: <Square size={14} />, etiqueta: "Tarea", color: "var(--info)" },
  DECISION: { icono: <Diamond size={14} />, etiqueta: "Decisión", color: "var(--warning)" },
  FIN: { icono: <Flag size={14} />, etiqueta: "Fin", color: "var(--fg-muted)" },
}

export function DiagramaFlujo({ pasos }: { pasos: Paso[] }) {
  if (pasos.length === 0) {
    return (
      <p className="py-8 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Este proceso todavía no tiene pasos.
      </p>
    )
  }

  return (
    <ol className="flex flex-col items-center py-2">
      {pasos.map((paso, i) => {
        const forma = FORMA[paso.tipo] ?? FORMA.TAREA
        const esDecision = paso.tipo === "DECISION"
        return (
          <li key={paso.id} className="flex w-full flex-col items-center">
            <div
              className="w-full max-w-[420px] superficie px-3 py-2"
              style={{
                borderLeftWidth: "3px",
                borderLeftColor: forma?.color,
                // El rombo se sugiere con las esquinas, no con una rotación: rotar la
                // caja rotaría también el texto.
                borderRadius: esDecision ? "14px" : undefined,
              }}
            >
              <p
                className="flex items-center gap-1 text-[11px] font-semibold uppercase"
                style={{ color: forma?.color }}
              >
                {forma?.icono}
                {forma?.etiqueta}
              </p>
              <p className="mt-1 text-sm font-medium">{paso.titulo}</p>
              {paso.descripcion && (
                <p className="mt-0.5 text-xs" style={{ color: "var(--fg-muted)" }}>
                  {paso.descripcion}
                </p>
              )}
              {paso.responsable && (
                <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                  Responsable: {paso.responsable}
                </p>
              )}
              {esDecision && (paso.rama_si || paso.rama_no) && (
                <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="font-semibold">Sí</dt>
                    <dd style={{ color: "var(--fg-muted)" }}>{paso.rama_si ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">No</dt>
                    <dd style={{ color: "var(--fg-muted)" }}>{paso.rama_no ?? "—"}</dd>
                  </div>
                </dl>
              )}
            </div>
            {i < pasos.length - 1 && (
              <span
                aria-hidden="true"
                className="h-6 w-px"
                style={{ backgroundColor: "var(--border)" }}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}
